import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Alert, Select, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { listFailedMessagesInfiniteOptions } from "../../../services/client/@tanstack/react-query.gen";
import type {
  FailedMessage,
  FailureReason,
  MessageType,
} from "../../../services/client/types.gen";
import {
  FETCH_PAGE_SIZE,
  useCursorPagination,
} from "../../../hooks/useCursorPagination";
import { extractErrorMessage } from "../../../util/extractErrorMessage";
import { FailedMessageDetails } from "./FailedMessageDetails";
import {
  FAILURE_REASON_COLORS,
  FAILURE_REASON_LABELS,
  MESSAGE_TYPE_LABELS,
} from "./constants";
import { formatDateTime } from "../../../util/formatDate";

const summarise = (failedMessage: FailedMessage) => {
  const [firstError] = failedMessage.parsing_errors;
  if (firstError) {
    const remaining = failedMessage.parsing_errors.length - 1;
    return `${firstError.location}: ${firstError.message}${
      remaining > 0 ? ` (+${remaining} more)` : ""
    }`;
  }
  return failedMessage.error?.split("\n").filter(Boolean).at(-1) ?? "—";
};

export const FailedMessagesTab = ({ site_id }: { site_id: string }) => {
  const [types, setTypes] = useState<MessageType[]>([]);
  const [reasons, setReasons] = useState<FailureReason[]>([]);

  const {
    data,
    error,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    ...listFailedMessagesInfiniteOptions({
      query: {
        site_id: [site_id],
        page_size: FETCH_PAGE_SIZE,
        type: types.length > 0 ? types : null,
        reason: reasons.length > 0 ? reasons : null,
      },
    }),
    getNextPageParam: (lastPage) => lastPage.end_cursor,
  });

  const { dataSource, pagination, isPageLoading } =
    useCursorPagination<FailedMessage>({
      pages: data?.pages,
      hasNextPage: hasNextPage ?? false,
      fetchNextPage,
      isFetchingNextPage,
    });

  const columns: ColumnsType<FailedMessage> = [
    {
      title: "Received",
      dataIndex: "received_at",
      key: "received_at",
      width: 190,
      render: (receivedAt: string | null) =>
        receivedAt ? formatDateTime(receivedAt) : "—",
    },
    {
      title: "Asset",
      dataIndex: "asset_id",
      key: "asset_id",
      render: (assetId: string | null) => assetId ?? "—",
    },
    {
      title: "Message",
      dataIndex: "type",
      key: "type",
      render: (type: MessageType) => (
        <Tag>{MESSAGE_TYPE_LABELS[type] ?? type}</Tag>
      ),
    },
    {
      title: "Problem",
      dataIndex: "reason",
      key: "reason",
      render: (reason: FailureReason) => (
        <Tag color={FAILURE_REASON_COLORS[reason]}>
          {FAILURE_REASON_LABELS[reason] ?? reason}
        </Tag>
      ),
    },
    {
      title: "Details",
      key: "summary",
      render: (_, failedMessage) => (
        <Typography.Text type="secondary" ellipsis>
          {summarise(failedMessage)}
        </Typography.Text>
      ),
    },
  ];

  return (
    <div className="w-full">
      <Typography.Paragraph type="secondary">
        Messages this site published over MQTT that we could not process. Expand
        a row to see what we received and why it was rejected.
      </Typography.Paragraph>

      <Space wrap className="mb-4">
        <Select<MessageType[]>
          mode="multiple"
          allowClear
          className="min-w-48"
          placeholder="All message types"
          options={Object.entries(MESSAGE_TYPE_LABELS).map(
            ([value, label]) => ({ value: value as MessageType, label }),
          )}
          value={types}
          onChange={setTypes}
        />
        <Select<FailureReason[]>
          mode="multiple"
          allowClear
          className="min-w-48"
          placeholder="All problems"
          options={Object.entries(FAILURE_REASON_LABELS).map(
            ([value, label]) => ({ value: value as FailureReason, label }),
          )}
          value={reasons}
          onChange={setReasons}
        />
      </Space>

      {!!error && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Failed to load failed messages"
          description={extractErrorMessage(error)}
        />
      )}

      <Table<FailedMessage>
        columns={columns}
        dataSource={dataSource}
        loading={isLoading || isPageLoading}
        pagination={pagination}
        rowKey="id"
        expandable={{
          expandedRowRender: (failedMessage) => (
            <FailedMessageDetails failedMessage={failedMessage} />
          ),
        }}
        locale={{
          emptyText: "No failed messages — everything is coming in fine.",
        }}
      />
    </div>
  );
};
